import {studioErrorText} from './studioErrorText';

/*
 * How a failed Studio request reads for authors: the error the Studio repository returns, and
 * the check that keeps provider, worker and server detail out of what authors see.
 */

/** What authors see when Studio fails for a reason they cannot act on. */
export const GENERIC_ERROR = studioErrorText.genericError;
/**
 * A pending credit check refuses AI actions until the AI credits a stopped generation holds
 * settle. Said this way when the server does not say why (see the Studio's `creditCheck`).
 */
export const CREDIT_CHECK_PENDING = studioErrorText.creditCheckPending;

/** The code of a request refused because the workspace has too few AI credits for it. */
export const INSUFFICIENT_CREDITS = 'insufficient_credits';
/** The code of a request refused while the job's credit check is pending. */
export const CREDIT_CHECK_PENDING_CODE = 'credit_check_pending';

export class ApiError extends Error {
  status: number;
  /**
   * True when the server answered with its own message, even one replaced by
   * GENERIC_ERROR; a server without the route answers with a bare status.
   */
  fromServer: boolean;
  /** What the server named the refusal, e.g. INSUFFICIENT_CREDITS; older servers name none. */
  code?: string;

  constructor(
    message: string,
    status: number,
    fromServer = false,
    code?: string,
  ) {
    super(message);
    this.status = status;
    this.fromServer = fromServer;
    this.code = code;
  }
}

/** The request was refused because the workspace needs more AI credits for it. */
export const isInsufficientCredits = (error: unknown) =>
  error instanceof ApiError && error.code === INSUFFICIENT_CREDITS;

/** The request was refused because the job's credit check is pending. */
export const isCreditCheckPending = (error: unknown) =>
  error instanceof ApiError && error.code === CREDIT_CHECK_PENDING_CODE;

// Provider, worker, storage and billing detail that older Studio servers (and
// errors they saved on jobs) pass on. Messages written for authors never say these.
const TECHNICAL = [
  /open ?router|open ?ai|eleven ?labs|anthropic|azure|kokoro|whisper/i,
  /\b[A-Z][A-Z0-9]*_[A-Z0-9_]+\b/, // environment settings such as OPENROUTER_API_KEY
  /api[ _-]?key/i,
  /\bproviders?\b|\bworkers?\b|reconcil|budget|tariff|tenant/i,
  /exception|unexpected error|stack trace|traceback|stderr|ffmpeg|ffprobe/i,
  /https?:\/\/|\bjson\b|\bsql\b|\bgcs\b|\bbucket\b|checksum|manifest/i,
  /\(\d{3}\)|status code|time(?:d)? ?out|object reference/i,
  /\bfile measures\b/i, // an internal check of the rendered file's timing
  // Process output: exit codes, stack frames, tracebacks, JSON and server paths.
  /\bfailed \(-?\d+\)|^\s*at\s|\bline \d+, in\b|\b[A-Z]\w*Error\b|^\s*[[{]/m,
  /(?:^|[\s'(=])(?:\/[\w.-]+){2,}|\b[A-Z]:\\/m,
  // Storage, setup and request checks of the server itself.
  /\brenderer\b|\breservation\b|\bworkspace (?:path|object|links)\b|\bskills\//i,
  /\bscheduler\b|scratch workspace|\bstored object\b|\bmusic asset\b/i,
  /(?:not configured for|cannot be used in) production|\b(?:integration|pricing) is not configured/i,
  /(?:requires? an? \w+|missing \w+) context\b/i,
  /^(?:missing body|unsupported stage)\.?$/im,
  // The provider cost guard and billing of older servers ("Estimated provider cost USD 4.20
  // exceeds this job's remaining safety budget USD 1.33. This limit is separate from your
  // workspace AI credits."). The only limit authors are told about is their AI credits.
  /\b(?:estimated|actual) (?:\w+ )?cost\b|\brender estimate\b|\b(?:cost|spend(?:ing)?|key) limit/i,
  /\bUSD\b|\bUS\$|\bsafety (?:budget|limit)|separate from your workspace AI credits/i,
];

/**
 * True when a message carries technical detail. Quoted text is the author's own
 * document ("Quote on p. 3 does not match…"), so it never counts. A quote that
 * ends a line (`…page text: "Press "Reset" now".`) runs to its last quote mark,
 * since the document text may contain quote marks of its own.
 */
export function looksTechnical(text: string) {
  const own = text
    .replace(/: "[^\n]*"(?=\.?$)/gm, ': ""')
    .replace(/"[^"\n]*"|“[^”\n]*”/g, '""');
  return TECHNICAL.some(pattern => pattern.test(own));
}

// Older servers prefixed the worker's document checks, which are written for authors.
const WORKER_DOCUMENT_CHECK =
  /^Worker \/(?:ingest|ingest-text|extract-brand) failed \((?:413|415|422)\):\s*/;

/**
 * A server message an author can read, or GENERIC_ERROR. A list such as "Resolve
 * these before rendering:" followed by "- " items keeps the items an author can
 * fix; any other message is hidden whole when any of its lines is technical, so
 * nothing of a process failure or stack trace shows.
 */
export function safeMessage(text: string | null | undefined): string {
  const lines = (text || '')
    .trim()
    .replace(WORKER_DOCUMENT_CHECK, '')
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean);
  const [heading = '', ...items] = lines;
  if (
    heading.endsWith(':') &&
    items.length &&
    items.every(item => item.startsWith('- ')) &&
    !looksTechnical(heading)
  ) {
    const kept = items.filter(item => !looksTechnical(item.slice(2)));
    return kept.length ? [heading, ...kept].join('\n') : GENERIC_ERROR;
  }
  const message = lines.join('\n');
  // A heading alone says nothing.
  return message && !message.endsWith(':') && !looksTechnical(message)
    ? message
    : GENERIC_ERROR;
}
